import * as React from 'react';

import { CACHE_MS, NO_OPTIONS, optionCache, remember } from './filter-option-cache';
import type { AsyncFilterDef, FilterOption } from './types';
import { useDebouncedValue } from './use-debounced-value';

interface OptionsState {
  key: string;
  options: readonly FilterOption[];
  status: 'loading' | 'ready' | 'error';
}

/** Options of an async filter for the picker's search text. */
export function useFilterOptions(
  def: AsyncFilterDef<never> | undefined,
  query: string,
  enabled = true,
) {
  const debounced = useDebouncedValue(query.trim(), def?.debounceMs ?? 250);
  const requestKey = def && enabled ? `${def.key}\u0000${debounced}` : null;
  const [state, setState] = React.useState<OptionsState | null>(null);
  // A cached answer shows at once; the effect refreshes it once it is stale.
  const cached = requestKey ? optionCache.get(requestKey)?.options : undefined;

  React.useEffect(() => {
    if (!def || !requestKey) return;
    const entry = optionCache.get(requestKey);
    if (entry && Date.now() - entry.at < CACHE_MS) return;
    const controller = new AbortController();
    def.loadOptions(debounced, controller.signal).then(
      (options) => {
        if (controller.signal.aborted) return;
        optionCache.set(requestKey, { at: Date.now(), options });
        remember(def.key, options);
        setState({ key: requestKey, options, status: 'ready' });
      },
      () => {
        if (controller.signal.aborted) return;
        setState((prev) => ({
          key: requestKey,
          options: prev?.options ?? NO_OPTIONS,
          status: 'error',
        }));
      },
    );
    return () => controller.abort();
  }, [debounced, def, requestKey]);

  if (cached) return { options: cached, isLoading: false, isError: false };
  // While a new query loads, keep showing the previous result (no flicker).
  const current = state?.key === requestKey ? state : null;
  return {
    options: current?.options ?? state?.options ?? NO_OPTIONS,
    isLoading: requestKey !== null && current === null,
    isError: current?.status === 'error',
  };
}
